<template>
  <tabs-horizontal class="py-100">
    <!-- Shareable Items -->
    <b-tab-item label="Shared Items">
      <section-box>
        <div class="section_box--header flex flex-between flex-vertical-center">
          <p class="section_box--text">These are items of yours that will be shared with others. As you make changes those you share with will be able to download the latest version.</p>
          <button-default
            :loading="savingSharedItems"
            :disabled="savingSharedItems"
            @click="updateShareableItems"
          >Update Items</button-default>
        </div>

        <shareable-asset-list
          label="Pipeline"
          icon="pipeline"
          :shareable-assets="shareableAssets.pipelineViews"
          count-key="statusCount"
          count-label="Statuses"
        ></shareable-asset-list>

        <shareable-asset-list
          label="Follow Up Sequences"
          icon="follow-up"
          :shareable-assets="shareableAssets.followUpSequenceTemplates"
          count-key="stepsCount"
          count-label="Steps"
        ></shareable-asset-list>

        <shareable-asset-list
          label="Call Scripts"
          icon="call-scripts"
          :shareable-assets="shareableAssets.callScripts"
          count-key="stepsCount"
          count-label="Steps"
        ></shareable-asset-list>

        <shareable-asset-list
          label="Snippets"
          icon="snippets"
          :shareable-assets="shareableAssets.snippets"
          count-key="type"
        ></shareable-asset-list>
      </section-box>
    </b-tab-item>

    <!-- Shared Email Addresses -->
    <b-tab-item label="Shared With">
      <section-box>
        <div class="section_box--header">
          <h3 class="section_box--heading text-uppercase">Add New Accounts</h3>

          <form-field-textarea
            v-model="sharedWithForm.email_addresses"
            :validation-errors="sharedWithFormValidationErrors.email_addresses"
            placeholder="i.e. danny@example.com, john@doe.com, susanseller@example.com"
            help-text="Enter a comma seperated list of emails for people who you want to share this group with."
          ></form-field-textarea>

          <div class="flex flex-end">
            <button-default @click="addEmailAddresses">Add Accounts</button-default>
          </div>
        </div>

        <h3 class="section_box--heading text-uppercase mt-200">Already Shared With</h3>
        <p class="section_box--text mb-100">These are the users that currently have access to this shared group in their store.</p>

        <app-empty-message
          :count="sharedWithData.length"
          heading="You haven't shared this group with anyone yet"
        >
          <table-default
            :table-data="sharedWithData"
            default-sort="label"
          >
            <b-table-column label="Email" field="email_address" sortable v-slot="props">
              {{ props.row.email_address }}
            </b-table-column>

            <b-table-column label="Shared Date" v-slot="props">
              {{ props.row.created_at }}
            </b-table-column>

            <b-table-column cell-class="action_cell" width="120" v-slot="props">
              <button-link @click="unShare(props.row)">unshare</button-link>
            </b-table-column>
          </table-default>
        </app-empty-message>
      </section-box>
    </b-tab-item>
  </tabs-horizontal>
</template>

<script>
import FormValidationErrors from "../../../components/form/FormValidationErrors";
import TablePaginated from "../../../components/table/TablePaginated";
import api from "../../../api";
import ShareableAssetList from "./ShareableAssetList";
import {MODELS} from "../../../services/models";
import {ENUMS} from "../../../services/enums";

export default {
  name: "ShareableGroupEditModal",
  components: {
    ShareableAssetList,
    FormValidationErrors,
    TablePaginated,
  },
  props: {
    selectedShareableGroup: {
      type: Object,
      required: true
    },
  },
  mounted() {
    this.loadSharedWith();
    this.mapSharedAssets();
  },
  data() {
    return {
      savingSharedItems: false,
      sharedWithForm: {
        email_addresses: '',
      },
      sharedWithFormValidationErrors: {},
      sharedWithData: [],
      shareableAssets: {
        pipelineViews: [],
        followUpSequenceTemplates: [],
        callScripts: [],
        snippets: [],
      },
    }
  },
  computed: {
    selectedSharedAssets() {
      return [
        ...this.shareableAssets.pipelineViews,
        ...this.shareableAssets.followUpSequenceTemplates,
        ...this.shareableAssets.snippets,
        ...this.shareableAssets.callScripts,
      ].filter((asset) => asset.shared);
    }
  },
  methods: {
    updateShareableItems() {
      let request = {
        id: this.selectedShareableGroup.id,
        shared_assets: this.selectedSharedAssets
      };

      this.savingSharedItems = true;
      return api.shareableGroups.updateSharedAssets(request).then(() => {
        this.$emit('saved');
      }).finally(() => {
        this.savingSharedItems = false;
      });
    },
    addEmailAddresses() {
      let data = this.$utils.clone(this.sharedWithForm);
      data.email_addresses = data.email_addresses.split(',').map(email => email.trim())
      return api.shareableGroupEmails.create(this.selectedShareableGroup.id, data, this.sharedWithFormValidationErrors).then(() => {
        this.loadSharedWith();
        this.sharedWithForm = {
          email_addresses: '',
        };
      });
    },
    unShare(sharedEmail) {
      api.shareableGroupEmails.delete(sharedEmail.id).then(() => {
        this.loadSharedWith();
      });
    },
    loadSharedWith() {
      api.shareableGroupEmails.all(this.selectedShareableGroup.id).then(response => {
        this.sharedWithData = response.data;
      });
    },
    mapSharedAssets() {
      this.shareableAssets.pipelineViews = this.$store.getters["pipeline/pipelineViews"].map((pipelineView) => {
        return {
          class_name: MODELS.ShareableAssetJson.class_name,
          asset_type: MODELS.PipelineView.class_name,
          asset_id: pipelineView.id,
          label: pipelineView.label,
          description: pipelineView.description,
          statusCount: pipelineView.status_ids.length,
          shared: !!this.selectedShareableGroup.shared_assets.find((asset) => {
            return (asset.asset_type === MODELS.PipelineView.class_name && asset.asset_id === pipelineView.id);
          })
        }
      });

      this.shareableAssets.followUpSequenceTemplates = this.$store.getters.fusTemplates.map((fusTemplate) => {
        return {
          class_name: MODELS.ShareableAssetJson.class_name,
          asset_type: MODELS.FollowUpSequenceTemplate.class_name,
          asset_id: fusTemplate.id,
          label: fusTemplate.label,
          description: fusTemplate.description,
          stepsCount: fusTemplate.step_count,
          data: fusTemplate,
          shared: !!this.selectedShareableGroup.shared_assets.find((asset) => {
            return (asset.asset_type === fusTemplate.class_name && asset.asset_id === fusTemplate.id);
          })
        }
      });

      this.shareableAssets.callScripts = this.$store.getters.callScripts.map((callScript) => {
        return {
          class_name: MODELS.ShareableAssetJson.class_name,
          asset_id: callScript.id,
          asset_type: callScript.class_name,
          label: callScript.name,
          description: callScript.description,
          stepsCount: callScript.number_of_steps,
          data: callScript,
          shared: !!this.selectedShareableGroup.shared_assets.find((asset) => {
            return (asset.asset_type === callScript.class_name && asset.asset_id === callScript.id);
          })
        }
      });

      this.shareableAssets.snippets = this.$store.getters.snippets.map((snippet) => {
        return {
          class_name: MODELS.ShareableAssetJson.class_name,
          asset_id: snippet.id,
          asset_type: snippet.class_name,
          label: snippet.label,
          description: snippet.description,
          type: this.$utils.newGetEnumLabel(snippet.type, ENUMS.SnippetTypeEnum),
          data: snippet,
          shared: !!this.selectedShareableGroup.shared_assets.find((asset) => {
            return (asset.asset_type === snippet.class_name && asset.asset_id === snippet.id);
          })
        }
      });
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/variables/variables";

.section_box {

  &--header {
    margin-right: -$modalGutter;
    margin-left: -$modalGutter;
    padding: 0 $modalGutter $modalGutter;
    border-bottom: 1px solid $border;
  }

  &--text {
    max-width: 600px;
    margin-right: 2rem;
    line-height: 1.3;
  }

  &--heading {
    margin-bottom: 8px;
    color: $text-light;
  }
}
</style>
